import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  ComparatorBuilder,
  ElderDataToolbarComponent,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { CapabilitySpec } from '../../capabilities/capability-spec'
import { Router } from '@angular/router'
import { CapabilitySpecService } from '../../capability-spec.service'
import { BehaviorSubject, Observable } from 'rxjs'
import { ConstraintSpec } from '../constraint-spec'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import {
  ConstraintSpecDetailDialogComponent,
  IConstraintDialogOptions,
} from '../constraint-spec-detail-dialog/constraint-spec-detail-dialog.component'
import { ConstraintUtils } from '../constraint-utils'
import { PropertyUtil } from '../../../../properties/property-util'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { AsyncPipe } from '@angular/common'
import { MatTooltip } from '@angular/material/tooltip'
import { MatIcon } from '@angular/material/icon'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort } from '@angular/material/sort'

@Component({
  selector: 'app-constraint-spec-table',
  templateUrl: './constraint-spec-table.component.html',
  styleUrls: ['./constraint-spec-table.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatTooltip,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ConstraintSpecTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _constraints$: BehaviorSubject<ConstraintSpec[]> = new BehaviorSubject<
    ConstraintSpec[]
  >([])

  @Output()
  public constraintsChange: EventEmitter<ConstraintSpec[]> = new EventEmitter<ConstraintSpec[]>()

  public PropertyUtil = PropertyUtil
  private router = inject(Router)
  private toastService = inject(ElderToastService)
  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get constraints$(): Observable<ConstraintSpec[]> {
    return this._constraints$.asObservable()
  }

  public get constraints(): ConstraintSpec[] {
    return this._constraints$.getValue()
  }

  @Input()
  public set constraints(constraints: ConstraintSpec[]) {
    this._constraints$.next(constraints?.sort(ComparatorBuilder.fieldSort('description')))
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openSideDetail(spec: CapabilitySpec): void {
    this.toastService.info('Not implemented!')
  }

  public openDetail(spec: CapabilitySpec): void {
    this.router.navigate([{ outlets: { side: ['capability-specs', spec.id] } }])
  }

  public onClick(constraintSpec: ConstraintSpec): void {
    const dialogRef = this.openDialog(constraintSpec)
    dialogRef.afterClosed().subscribe((constraint: ConstraintSpec) => {
      if (constraint) {
        const constraintToUpdate = this.constraints.find((c) => c.id === constraint.id)
        ConstraintUtils.update(constraint, constraintToUpdate)
        this.constraintsChange.emit(this.constraints)
      }
    })
  }

  public onNew(event: Event): void {
    const dialogRef = this.openDialog(new ConstraintSpec())
    dialogRef.afterClosed().subscribe((constraint: ConstraintSpec) => {
      if (constraint) {
        this.addConstraint(constraint)
      }
    })
  }

  public onRemove(constraints: ConstraintSpec[]): void {
    this.removeConstraints(constraints)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private addConstraint(toAdd: ConstraintSpec) {
    const constraints = [...this.constraints]
    constraints.push(toAdd)
    this.constraints = constraints
    this.constraintsChange.emit(this.constraints)
  }

  private removeConstraints(toRemove: ConstraintSpec[]) {
    const constraints = [...this.constraints]
    toRemove.forEach((c) => constraints.splice(constraints.indexOf(c), 1))
    this.constraints = constraints
    this.constraintsChange.emit(this.constraints)
  }

  private openDialog(
    constraintSpec: ConstraintSpec
  ): MatDialogRef<ConstraintSpecDetailDialogComponent> {
    return this.dialog.open(ConstraintSpecDetailDialogComponent, {
      minWidth: '35vw',
      minHeight: '35vh',
      data: <IConstraintDialogOptions>{
        constraint: constraintSpec,
      },
    })
  }
}
